<template>
  <div class="page-wrap" v-if="loaded">

    <!-- ── Page Header ── -->
    <div class="page-header">
      <div class="page-header__left">
        <button class="btn-back" @click="$router.push('/collections/')">
          <i class="fa fa-arrow-left"></i>
        </button>
        <div>
          <h1 class="page-title">Edit Collection</h1>
          <span class="page-subtitle">{{ form.title || 'Untitled' }}</span>
        </div>
      </div>
      <div class="page-header__right">
        <button class="btn-translate" @click="showTranslator(id)">
          <i class="fa fa-language"></i> Translations
        </button>
      </div>
    </div>

    <!-- ── Basic Info ── -->
    <div class="panel">
      <div class="panel-title"><i class="fa fa-info-circle"></i> Basic Info</div>
      <div class="form-grid">
        <div class="form-field">
          <label class="filter-label">Title</label>
          <input v-model="form.title" class="dd-input" placeholder="Collection title" />
        </div>
        <div class="form-field">
          <label class="filter-label">Slug</label>
          <input v-model="form.slug" class="dd-input" placeholder="unique-url-slug" />
        </div>
        <div class="form-field">
          <label class="filter-label">Color</label>
          <select v-model="form.color" class="dd-input">
            <option :value="color" v-for="color in colors" :key="color">
              {{ color.charAt(0).toUpperCase() + color.slice(1) }}
            </option>
          </select>
        </div>
        <div class="form-field">
          <label class="filter-label">Youtube URL</label>
          <input v-model="form.video_url" class="dd-input" placeholder="https://youtube.com/..." />
        </div>
        <div class="form-field">
          <label class="filter-label">Price (AED)</label>
          <div class="input-with-suffix">
            <input v-model="form.price" type="text" class="dd-input dd-input--suffix" placeholder="0.00" />
            <span class="input-suffix">AED</span>
          </div>
        </div>
        <div class="form-field">
          <label class="filter-label">Default People Count</label>
          <input v-model="form.default_people_count" type="number" min="1" class="dd-input" />
        </div>
      </div>
    </div>

    <!-- ── Rating ── -->
    <div class="panel">
      <div class="panel-title"><i class="fa fa-star"></i> Rating</div>
      <div class="form-grid">
        <div class="form-field">
          <label class="filter-label">Rating Source</label>
          <input v-model="form.rating_source" class="dd-input" placeholder="e.g. tripadvisor" />
        </div>
        <div class="form-field">
          <label class="filter-label">Rating URL</label>
          <input v-model="form.rating_url" class="dd-input" placeholder="https://tripadvisor.com/..." />
        </div>
      </div>
    </div>

    <!-- ── Tags ── -->
    <div class="panel">
      <div class="panel-title"><i class="fa fa-tags"></i> Tags</div>
      <div class="form-field" style="max-width: 500px;">
        <label class="filter-label">Select Tags</label>
        <multiselect
            v-model="selectedTags"
            :options="tags"
            :multiple="true"
            :close-on-select="false"
            :clear-on-select="false"
            :preserve-search="true"
            placeholder="Pick tags..."
            label="title"
            track-by="id"
            :preselect-first="false"
            class="dd-multiselect"
        ></multiselect>
      </div>
    </div>

    <!-- ── Products ── -->
    <div class="panel">
      <div class="panel-title"><i class="fa fa-compass"></i> Experiences</div>

      <div class="form-field" style="max-width: 400px; margin-bottom: 1rem;">
        <label class="filter-label">Search Experiences</label>
        <div class="input-icon-wrap">
          <i class="fa fa-search input-icon"></i>
          <input
              v-model="search"
              class="dd-input dd-input--icon"
              placeholder="Search by title..."
              @keyup="loadExperiences"
          />
        </div>
      </div>

      <div class="exp-list" v-if="experiences.length">
        <div class="exp-list__item" v-for="exp in experiences" :key="exp.id">
          <span class="exp-list__name">{{ exp.title }}</span>
          <div class="exp-list__levels">
            <span
                class="level-badge"
                v-for="sl in exp.service_levels"
                :key="sl.id"
                @click="addProduct(exp.id, exp.title, sl.id, sl.title, exp.thumbnail.url)"
            >{{ sl.title }}</span>
          </div>
        </div>
      </div>

      <div v-if="products.length" class="selected-products">
        <div class="selected-label">Selected ({{ products.length }})</div>
        <div class="selected-list">
          <span
              class="selected-item"
              v-for="product in products"
              :key="product.service_level_id"
              @click="removeProduct(product.service_level_id)"
              title="Click to remove"
          >
            {{ product.product_title }}: {{ product.service_level_title }}
            <i class="fa fa-times"></i>
          </span>
        </div>
      </div>
    </div>

    <!-- ── Content ── -->
    <div class="panel">
      <div class="panel-title"><i class="fa fa-file-text"></i> Content</div>
      <div class="form-field mb-4">
        <label class="filter-label">Short Description</label>
        <vue-editor v-model="form.short_description" placeholder="Short description" />
      </div>
      <div class="form-field mb-4">
        <label class="filter-label">Description</label>
        <vue-editor v-model="form.description" placeholder="Full description" />
      </div>
      <div class="form-field mb-4">
        <label class="filter-label">Need to Know</label>
        <vue-editor v-model="form.need_to_know" placeholder="Need to know" />
      </div>
      <div class="form-field" style="max-width: 500px;">
        <label class="filter-label">Highlights</label>
        <array-box
            :items="form.highlights ?? []"
            @update="(arr) => (form.highlights = arr)"
        ></array-box>
      </div>
    </div>

    <!-- ── Photos ── -->
    <div class="panel">
      <div class="panel-title"><i class="fa fa-photo"></i> Photos</div>
      <MultipleFilesUpload
          :photos="form.images"
          @done="urls => form.photos = urls"
          label="Choose some photos..."
      ></MultipleFilesUpload>
    </div>

    <!-- ── Errors ── -->
    <ul class="error-list" v-if="errors.length">
      <li v-for="error in errors" :key="error">{{ error }}</li>
    </ul>

    <!-- ── Submit ── -->
    <div class="form-actions">
      <div class="publish-toggle">
        <label class="toggle-label">
          <input type="checkbox" v-model="form.is_published" class="toggle-input" />
          <span class="toggle-track"><span class="toggle-thumb"></span></span>
          <span class="toggle-text">{{ form.is_published ? 'Published' : 'Draft' }}</span>
        </label>
      </div>
      <button class="btn-cancel" @click="$router.push('/collections/')">
        <i class="fa fa-times"></i> Cancel
      </button>
      <button class="btn-save" @click="submit()">
        <i class="fa fa-check"></i> Save Changes
      </button>
    </div>

    <translator
        ref="translator"
        :keys="translateKeys"
        model="Collection"
        :model_id="modelID"
    ></translator>

  </div>
</template>

<script>
import { config } from "../helpers";
import ArrayBox from "../components/ArrayBox.vue";
import MultipleFilesUpload from "../components/MultipleFilesUpload.vue";
import multiselect from "vue-multiselect";
import Translator from "@/components/Translator.vue";
import { VueEditor } from "vue2-editor";

export default {
  name: "CollectionEdit",
  components: { Translator, multiselect, "array-box": ArrayBox, MultipleFilesUpload, VueEditor },
  data() {
    return {
      id: this.$route.params.id,
      url: "",
      loaded: false,
      search: "",
      tags: [],
      selectedTags: [],
      translateKeys: [
        { key: "title",             type: "text"  },
        { key: "short_description", type: "text"  },
        { key: "description",       type: "text"  },
        { key: "need_to_know",      type: "text"  },
        { key: "highlights",        type: "array" },
      ],
      modelID: 0,
      colors: ["white","yellow","orange","red","green","blue","purple","bronze","silver","gold","ace","exquisite","ultimate","couple"],
      categories: [],
      experiences: [],
      products: [],
      experiencesPage: 1,
      experiencesLastPage: 1,
      form: {
        title: "", slug: "", short_description: "", description: "",
        need_to_know: "", highlights: [], video_url: null,
        rating_url: null, rating_source: "tripadvisor",
        price: null, default_people_count: 1,
        images: null, products: [], is_published: false, color: null, tags: [],
      },
      errors: [],
    };
  },

  mounted() {
    this.url = config("BACKEND_ROOT");
    this.loadCollection();
    this.loadCategories();
    this.loadTags();
  },

  methods: {
    loadCollection() {
      axios.get(`${config("BACKEND_ROOT")}/admin/collections/${this.id}`).then((r) => {
        this.form = r.data.data;
        this.form.rating_source = this.form.rating_source || "tripadvisor";
        this.form.photos = this.form.images.map((img) => "files" + img.url.substring(img.url.lastIndexOf("/")));
        this.products = this.form.products.map((p) => ({
          product_id:          p.id,
          product_title:       p.title,
          service_level_id:    p.service_levels.length > 0 ? p.service_levels[0].id : "",
          service_level_title: p.service_levels.length > 0 ? p.service_levels[0].title : "",
          thumbnail_url:       p.thumbnail.url,
        }));
        this.form.price    = this.form.price.amount;
        this.selectedTags  = this.form.tags;
        this.loaded = true;
      });
    },

    loadCategories() { axios.get(`${config("BACKEND_ROOT")}/categories`).then((r) => { this.categories = r.data.data; }); },
    loadTags()       { axios.get(`${this.url}/admin/tags`).then((r) => { this.tags = r.data.data; }); },

    loadExperiences() {
      axios
          .get(`${config("BACKEND_ROOT")}/admin/products`, { params: { "filters[title]": this.search } })
          .then((r) => { this.experiences = r.data.data; this.experiencesLastPage = r.data.meta.last_page; });
    },

    showTranslator(modelId) {
      this.modelID = parseInt(modelId);
      this.$refs.translator.showTranslateModal();
    },

    addProduct(productId, productTitle, serviceLevelId, serviceLevelTitle, thumbnailUrl) {
      if (this.products.some((el) => el.service_level_id == serviceLevelId)) return alert("Already added!");
      this.products.push({ product_id: productId, product_title: productTitle, service_level_id: serviceLevelId, service_level_title: serviceLevelTitle, thumbnail_url: thumbnailUrl });
    },

    removeProduct(serviceLevelId) {
      if (confirm("Remove this item?")) {
        this.products = this.products.filter((el) => el.service_level_id != serviceLevelId);
      }
    },

    submit() {
      if (this.selectedTags.length > 0) this.form.tags = this.selectedTags.map((t) => t.id);
      this.form.products = this.products;
      axios
          .put(`${config("BACKEND_ROOT")}/admin/collections/${this.id}`, this.form)
          .then(() => this.$router.push("/collections"))
          .catch((e) => { this.errors = Object.values(e.response.data.errors).flat(); });
    },
  },
};
</script>

<style scoped>
/* ══════════════════════════════════════
   Page
══════════════════════════════════════ */
.page-wrap { display: flex; flex-direction: column; gap: 16px; }

/* ── Header ── */
.page-header { display: flex; align-items: flex-start; justify-content: space-between; flex-wrap: wrap; gap: 12px; }
.page-header__left { display: flex; align-items: flex-start; gap: 12px; }
.btn-back { width: 34px; height: 34px; margin-top: 4px; background: var(--bg-hover); border: 1px solid var(--border); border-radius: 8px; color: var(--text-2); cursor: pointer; display: flex; align-items: center; justify-content: center; flex-shrink: 0; transition: border-color .15s, color .15s; }
.btn-back:hover { border-color: var(--accent); color: var(--accent); }
.page-title   { font-size: 20px; font-weight: 500; color: var(--text-1); margin: 0 0 2px; }
.page-subtitle { font-size: 12px; color: var(--text-3); }
.page-header__right { display: flex; align-items: center; gap: 8px; }
.btn-translate { height: 36px; padding: 0 14px; background: rgba(129,140,248,.1); border: 1px solid rgba(129,140,248,.25); border-radius: 8px; color: #818cf8; font-size: 13px; font-weight: 500; cursor: pointer; display: flex; align-items: center; gap: 6px; transition: background .15s; }
.btn-translate:hover { background: rgba(129,140,248,.2); }

/* ── Panel ── */
.panel { background: var(--bg-panel); border: 1px solid var(--border); border-radius: 12px; padding: 1.25rem; }
.panel-title { font-size: 12px; font-weight: 600; color: var(--text-3); text-transform: uppercase; letter-spacing: .5px; margin-bottom: 1.1rem; display: flex; align-items: center; gap: 7px; }

/* ── Form ── */
.form-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
.form-field { display: flex; flex-direction: column; gap: 5px; }
.mb-4 { margin-bottom: 1.25rem; }
.filter-label { font-size: 11px; font-weight: 500; color: var(--text-3); text-transform: uppercase; letter-spacing: .4px; }

.dd-input { height: 36px; background: var(--bg-base); border: 1px solid var(--border); border-radius: 8px; color: var(--text-1); font-size: 13px; padding: 0 10px; outline: none; width: 100%; transition: border-color .15s; }
.dd-input:focus { border-color: var(--accent); }
.dd-input::placeholder { color: var(--text-3); }
.input-icon-wrap { position: relative; }
.input-icon { position: absolute; left: 10px; top: 50%; transform: translateY(-50%); color: var(--text-3); font-size: 13px; }
.dd-input--icon { padding-left: 32px; }
.input-with-suffix { display: flex; }
.dd-input--suffix { border-radius: 8px 0 0 8px; }
.input-suffix { height: 36px; padding: 0 12px; background: var(--bg-hover); border: 1px solid var(--border); border-left: none; border-radius: 0 8px 8px 0; display: flex; align-items: center; font-size: 13px; color: var(--text-3); }

/* Multiselect */
.dd-multiselect >>> .multiselect__tags { background: var(--bg-base); border: 1px solid var(--border); border-radius: 8px; min-height: 36px; }
.dd-multiselect >>> .multiselect__input, .dd-multiselect >>> .multiselect__single { background: transparent; color: var(--text-1); }
.dd-multiselect >>> .multiselect__placeholder { color: var(--text-3); }
.dd-multiselect >>> .multiselect__content-wrapper { background: var(--bg-panel); border: 1px solid var(--border); }
.dd-multiselect >>> .multiselect__option--highlight { background: var(--accent); color: #fff; }
.dd-multiselect >>> .multiselect__tag { background: var(--accent); border-radius: 20px; }

/* ── Experiences ── */
.exp-list { border: 1px solid var(--border); border-radius: 10px; overflow: hidden; max-height: 220px; overflow-y: auto; margin-bottom: 1rem; scrollbar-width: thin; }
.exp-list__item { display: flex; align-items: center; flex-wrap: wrap; gap: 8px; padding: 10px 14px; border-bottom: 1px solid var(--border); }
.exp-list__item:last-child { border-bottom: none; }
.exp-list__item:hover { background: var(--bg-hover); }
.exp-list__name { font-size: 13px; font-weight: 500; color: var(--text-1); }
.exp-list__levels { display: flex; flex-wrap: wrap; gap: 5px; }
.level-badge { display: inline-flex; padding: 3px 9px; border-radius: 20px; font-size: 11px; font-weight: 500; background: rgba(129,140,248,.12); color: #818cf8; border: 1px solid rgba(129,140,248,.25); cursor: pointer; transition: background .15s; }
.level-badge:hover { background: rgba(129,140,248,.25); }

.selected-products { border-top: 1px solid var(--border); padding-top: 1rem; margin-top: .5rem; }
.selected-label { font-size: 11px; font-weight: 600; color: var(--text-3); text-transform: uppercase; letter-spacing: .5px; margin-bottom: .6rem; }
.selected-list { display: flex; flex-wrap: wrap; gap: 6px; }
.selected-item { display: inline-flex; align-items: center; gap: 6px; padding: 4px 10px; border-radius: 20px; font-size: 12px; font-weight: 500; background: rgba(34,197,94,.1); color: #22c55e; border: 1px solid rgba(34,197,94,.25); cursor: pointer; transition: all .15s; }
.selected-item:hover { background: rgba(244,47,75,.1); color: #f42f4b; border-color: rgba(244,47,75,.25); }

/* ── Toggle ── */
.publish-toggle { display: flex; align-items: center; }
.toggle-label { display: flex; align-items: center; gap: 10px; cursor: pointer; }
.toggle-input { display: none; }
.toggle-track { width: 42px; height: 24px; background: var(--border-2); border-radius: 12px; position: relative; display: block; transition: background .2s; flex-shrink: 0; }
.toggle-input:checked + .toggle-track { background: var(--accent); }
.toggle-thumb { width: 18px; height: 18px; background: #fff; border-radius: 50%; position: absolute; top: 3px; left: 3px; transition: transform .2s; box-shadow: 0 1px 3px rgba(0,0,0,.2); }
.toggle-input:checked + .toggle-track .toggle-thumb { transform: translateX(18px); }
.toggle-text { font-size: 13px; font-weight: 500; color: var(--text-2); }

/* ── Errors / Actions ── */
.error-list { padding-left: 16px; color: var(--accent); font-size: 13px; }
.form-actions { display: flex; justify-content: flex-end; align-items: center; gap: 10px; }
.btn-cancel { height: 38px; padding: 0 18px; background: var(--bg-hover); border: 1px solid var(--border); border-radius: 9px; color: var(--text-2); font-size: 13px; cursor: pointer; display: flex; align-items: center; gap: 6px; }
.btn-cancel:hover { border-color: var(--border-2); }
.btn-save   { height: 38px; padding: 0 22px; background: var(--accent); border: none; border-radius: 9px; color: #fff; font-size: 13px; font-weight: 500; cursor: pointer; display: flex; align-items: center; gap: 6px; transition: opacity .15s; }
.btn-save:hover { opacity: .88; }

@media (max-width: 768px) {
  .form-grid { grid-template-columns: 1fr; }
}
</style>